/* lang-formants.js — vowel formant analysis (F1/F2/F3) for Timbrica
 * language tools (vowel-trainer).
 *
 * Pure DSP, no I/O, fully node-testable. The pipeline, per frame:
 *   1. down-sample to ~9 kHz  (formants of interest live below 4.5 kHz)
 *   2. pre-emphasis  y[n] = x[n] - 0.97 x[n-1]   (flattens the spectral tilt)
 *   3. Hamming window
 *   4. autocorrelation  R[0..p]
 *   5. Levinson-Durbin  → LPC all-pole coefficients A(z)
 *   6. Durand-Kerner root-finding of A(z)
 *   7. each complex root → formant frequency + bandwidth; keep the
 *      narrow-band roots in [90, 4500] Hz → F1, F2, F3
 *
 * Exposes window.Formants. Plain IIFE; also module.exports for node tests.
 */
(function (root, factory) {
  'use strict';
  var F = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = F;
  if (typeof window !== 'undefined') window.Formants = F;
  else if (root) root.Formants = F;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var TARGET_FS = 9000;        // analysis sample rate
  var PREEMPH   = 0.97;

  /* ── small helpers ─────────────────────────────────────────────── */

  function hamming(N) {
    var w = new Float64Array(N);
    for (var i = 0; i < N; i++) w[i] = 0.54 - 0.46 * Math.cos(2 * Math.PI * i / (N - 1));
    return w;
  }

  /* Average-decimate `x` from `fromFs` toward TARGET_FS. */
  function downsample(x, fromFs) {
    if (fromFs <= TARGET_FS * 1.05) return { data: x, fs: fromFs };
    var factor = Math.max(1, Math.round(fromFs / TARGET_FS));
    var outLen = Math.floor(x.length / factor);
    var out = new Float64Array(outLen);
    for (var i = 0; i < outLen; i++) {
      var s = 0;
      for (var k = 0; k < factor; k++) s += x[i * factor + k];
      out[i] = s / factor;
    }
    return { data: out, fs: fromFs / factor };
  }

  function autocorr(x, p) {
    var n = x.length, r = new Float64Array(p + 1);
    for (var k = 0; k <= p; k++) {
      var s = 0;
      for (var i = k; i < n; i++) s += x[i] * x[i - k];
      r[k] = s;
    }
    return r;
  }

  /* Levinson-Durbin: autocorrelation R → LPC coeffs A(z)=1+Σa[k]z^-k. */
  function levinson(r, p) {
    if (!(r[0] > 0)) return null;
    var a = new Float64Array(p + 1);
    a[0] = 1;
    var err = r[0];
    for (var i = 1; i <= p; i++) {
      var acc = r[i];
      for (var j = 1; j < i; j++) acc += a[j] * r[i - j];
      var k = -acc / err;
      if (!isFinite(k)) return null;
      var prev = a.slice(0);
      a[i] = k;
      for (var j2 = 1; j2 < i; j2++) a[j2] = prev[j2] + k * prev[i - j2];
      err *= (1 - k * k);
      if (err <= 0) err = 1e-9;
    }
    return a;
  }

  /* ── complex arithmetic (plain {re,im} objects) ────────────────── */

  function cAdd(a, b) { return { re: a.re + b.re, im: a.im + b.im }; }
  function cSub(a, b) { return { re: a.re - b.re, im: a.im - b.im }; }
  function cMul(a, b) {
    return { re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re };
  }
  function cDiv(a, b) {
    var d = b.re * b.re + b.im * b.im;
    if (d === 0) return { re: 0, im: 0 };
    return { re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d };
  }
  function cAbs(a) { return Math.sqrt(a.re * a.re + a.im * a.im); }

  /* Durand-Kerner: all roots of c[0]z^n + c[1]z^(n-1) + … + c[n]. */
  function rootsOf(coeffs) {
    var n = coeffs.length - 1;
    while (n > 0 && coeffs[0] === 0) { coeffs = coeffs.slice(1); n = coeffs.length - 1; }
    if (n < 1) return [];
    // monic
    var c = new Array(n + 1);
    for (var i = 0; i <= n; i++) c[i] = coeffs[i] / coeffs[0];

    function evalP(z) {
      var acc = { re: 1, im: 0 };
      for (var k = 1; k <= n; k++) acc = cAdd(cMul(acc, z), { re: c[k], im: 0 });
      return acc;
    }

    var roots = [];
    var seed = { re: 0.4, im: 0.9 }, cur = { re: 1, im: 0 };
    for (var r = 0; r < n; r++) { cur = cMul(cur, seed); roots.push({ re: cur.re, im: cur.im }); }

    for (var iter = 0; iter < 120; iter++) {
      var maxDelta = 0;
      for (var a = 0; a < n; a++) {
        var num = evalP(roots[a]);
        var den = { re: 1, im: 0 };
        for (var b = 0; b < n; b++) {
          if (b === a) continue;
          den = cMul(den, cSub(roots[a], roots[b]));
        }
        var delta = cDiv(num, den);
        roots[a] = cSub(roots[a], delta);
        var d = cAbs(delta);
        if (d > maxDelta) maxDelta = d;
      }
      if (maxDelta < 1e-12) break;
    }
    return roots;
  }

  /* ── single-frame formant estimate ─────────────────────────────── */

  /* samples: Float32Array/Array of one frame · fromFs: its sample rate.
   * returns {f1,f2,f3, formants:[{freq,bw}], lpcOrder, ok} */
  function analyzeFrame(samples, fromFs, opts) {
    opts = opts || {};
    if (!samples || samples.length < 64 || !(fromFs > 0)) {
      return { f1: null, f2: null, f3: null, formants: [], ok: false };
    }
    var ds = downsample(samples, fromFs);
    var x = ds.data, fs = ds.fs;
    var n = x.length;
    if (n < 48) return { f1: null, f2: null, f3: null, formants: [], ok: false };

    // pre-emphasis
    var pe = new Float64Array(n);
    pe[0] = x[0];
    for (var i = 1; i < n; i++) pe[i] = x[i] - PREEMPH * x[i - 1];

    // energy gate — silent frames carry no formants
    var energy = 0;
    for (var e = 0; e < n; e++) energy += pe[e] * pe[e];
    if (energy / n < (opts.energyFloor != null ? opts.energyFloor : 1e-7)) {
      return { f1: null, f2: null, f3: null, formants: [], ok: false, silent: true };
    }

    // window
    var w = hamming(n);
    for (var j = 0; j < n; j++) pe[j] *= w[j];

    // LPC order ≈ 2 + fs/1000 (one pole pair per ~1 kHz of bandwidth).
    // Higher orders fit spurious wide-band poles between the real formants.
    var p = opts.order || Math.min(n - 1, Math.round(fs / 1000) + 2);
    if (p < 8) p = 8;
    var a = levinson(autocorr(pe, p), p);
    if (!a) return { f1: null, f2: null, f3: null, formants: [], ok: false };

    var roots = rootsOf(Array.prototype.slice.call(a));
    var formants = [];
    for (var k = 0; k < roots.length; k++) {
      var z = roots[k];
      if (z.im <= 0) continue;                       // each conjugate pair once
      var mag = cAbs(z);
      if (mag >= 1.0) mag = 0.9999;                  // numerical guard
      var freq = Math.atan2(z.im, z.re) * fs / (2 * Math.PI);
      var bw   = -(fs / Math.PI) * Math.log(mag);
      // real vowel formants are narrow-band; wide poles are LPC artefacts
      if (freq >= 90 && freq <= 4500 && bw > 0 && bw < 400) {
        formants.push({ freq: freq, bw: bw });
      }
    }
    formants.sort(function (m, n2) { return m.freq - n2.freq; });

    return {
      f1: formants[0] ? Math.round(formants[0].freq) : null,
      f2: formants[1] ? Math.round(formants[1].freq) : null,
      f3: formants[2] ? Math.round(formants[2].freq) : null,
      formants: formants,
      lpcOrder: p,
      ok: formants.length >= 2
    };
  }

  /* ── whole-signal analysis (sliding window) ────────────────────── */

  /* signal: Float32Array · fs: sample rate.
   * opts: {frameMs=40, hopMs=20}
   * returns {f1,f2,f3 (medians of voiced frames), frames:[{t,f1,f2,f3}]} */
  function analyze(signal, fs, opts) {
    opts = opts || {};
    if (!signal || !signal.length || !(fs > 0)) {
      return { f1: null, f2: null, f3: null, frames: [] };
    }
    var frameMs = opts.frameMs || 40, hopMs = opts.hopMs || 20;
    var frameLen = Math.round(fs * frameMs / 1000);
    var hop = Math.round(fs * hopMs / 1000);
    var frames = [];
    for (var start = 0; start + frameLen <= signal.length; start += hop) {
      var seg = signal.subarray ? signal.subarray(start, start + frameLen)
                                : signal.slice(start, start + frameLen);
      var r = analyzeFrame(seg, fs, opts);
      if (r.ok) frames.push({ t: start / fs, f1: r.f1, f2: r.f2, f3: r.f3 });
    }
    function median(key) {
      var v = frames.map(function (f) { return f[key]; })
                    .filter(function (x) { return x != null; }).sort(function (a, b) { return a - b; });
      if (!v.length) return null;
      return Math.round(v[(v.length - 1) >> 1]);
    }
    return { f1: median('f1'), f2: median('f2'), f3: median('f3'), frames: frames };
  }

  /* Accept an AudioBuffer (or {sampleRate,getChannelData}) directly. */
  function analyzeBuffer(buffer, opts) {
    if (!buffer || typeof buffer.getChannelData !== 'function') {
      return { f1: null, f2: null, f3: null, frames: [] };
    }
    return analyze(buffer.getChannelData(0), buffer.sampleRate, opts);
  }

  return {
    analyzeFrame: analyzeFrame,
    analyze: analyze,
    analyzeBuffer: analyzeBuffer,
    _internal: { levinson: levinson, autocorr: autocorr, rootsOf: rootsOf, downsample: downsample }
  };
});
